import { useAuth0 } from '@auth0/auth0-react'
import axios from 'axios'
import { equals } from 'ramda'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useIdleTimeout } from '~shared-components/hooks/useIdleTimeout'
import { useInitLocale } from '~shared-components/hooks/useInitLocale'
import { useInitUser } from '~shared-components/hooks/useInitUser'
import { useLoggedInUser } from '~shared-components/hooks/useLoggedInUser'
import { useRedirect } from '~shared-components/hooks/useRedirect'
import { setLogoutTimestamp } from '~shared-components/hooks/useReloadIfLoggedOut'
import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { TypeOfTerms } from '~shared-components/translations/terms/terms'
import { Page } from '~shared-components/utils/enums'

const useAuthentication = () => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
  } = useAuth0()
  // `axios.defaults` is a plain global: writing the header re-renders nothing,
  // so the `useInitUser` gate below would never re-read it. Mirror it in state,
  // seeded from the global so a later instance starts out enabled.
  const [hasAuthorizationHeader, setHasAuthorizationHeader] = useState(
    () => axios.defaults.headers.common['Authorization'] !== undefined,
  )
  const onLogout = (showLogoutMessage = true) => {
    setLogoutTimestamp()
    logout({
      returnTo: `${window.location.origin}/oauth-redirect?prompt=login${
        showLogoutMessage ? '&action=logout' : ''
      }`,
    })
  }

  const auth0Audience = useSelector(ConfigSelector.auth0Audience)
  const pageType = useSelector(LocationSelector.pageType, equals)
  // There is a possible bug here. If user is active in another tab, this one would still log user out. It needs to be synced via local storage. :-P
  useIdleTimeout({ onIdle: () => onLogout(false) })

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect({ redirectUri: window.location.origin })
    }
  }, [isLoading, isAuthenticated, loginWithRedirect])

  useEffect(() => {
    const getToken = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: auth0Audience,
        })
        if (token && token.length) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          setHasAuthorizationHeader(true)
        } else {
          delete axios.defaults.headers.common['Authorization']
          setHasAuthorizationHeader(false)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }

    if (axios.defaults.headers.common.Authorization === undefined) {
      getToken()
    } else {
      setHasAuthorizationHeader(true)
    }
  }, [getAccessTokenSilently, auth0Audience])

  useInitUser({
    isEnabled: isAuthenticated && hasAuthorizationHeader,
    onError: () => onLogout(false),
  })

  const { isLoading: isLoadingUser, user } = useLoggedInUser()
  useInitLocale({ user })

  const redirect = useRedirect()

  useEffect(() => {
    const shouldRedirectToAccessRestricted = Boolean(
      user &&
        user.hasAcceptedTerms &&
        user.permissions.length === 0 &&
        pageType !== Page.AccessRestricted,
    )

    const shouldRedirectToTerms = Boolean(
      user && !user.hasAcceptedTerms && pageType !== Page.TermsAndConditions,
    )

    if (shouldRedirectToTerms) {
      redirect(Page.TermsAndConditions, {
        typeOfTerms: TypeOfTerms.TermsOfUse,
      })
    }

    if (shouldRedirectToAccessRestricted) {
      redirect(Page.AccessRestricted)
    }
  })

  const isLoadingAuthentication = isLoading || isLoadingUser

  return {
    isAuthenticated,
    isLoadingAuthentication,
    isLoadingUser,
    onLogout,
    user,
  }
}

export default useAuthentication
